import {
  type PropsWithChildren,
  type ReactNode,
  Suspense,
  lazy,
  useState,
} from 'react';

import { VisuallyHidden } from '@spotify-internal/encore-web';
import {
  FeatureFlagged,
  splitComponent,
  useFeatureFlag,
} from '@spotify/confidence-flag-evaluation-react';

import { useSetOnboardingGoal } from '../../contexts';
import { useIsWorkspaceAdmin } from '../../hooks/useIsWorkspaceAdmin';
import { useOnboardingSurvey } from '../../hooks/useOnboardingSurvey';
import { OnboardingProgress } from '../OnboardingProgress';
import { AgentWelcome, OnboardingSurvey } from '../OnboardingSurvey';

const ContextReview = lazy(() =>
  import('../ContextReview').then(module => ({
    default: module.ContextReview,
  })),
);

const PlanIntro = lazy(() =>
  import('../PlanIntro').then(module => ({
    default: module.PlanIntro,
  })),
);

const ONBOARDING_STEPS = [
  { id: 'about', label: 'About you' },
  { id: 'company', label: 'Your company' },
] as const;

type OnboardingStep = (typeof ONBOARDING_STEPS)[number]['id'];

type OnboardingSurveyGateProps = PropsWithChildren<{ fallback?: ReactNode }>;

const SurveyGate = ({
  children,
  fallback = null,
}: OnboardingSurveyGateProps) => {
  const { status, workspaceName, isSaving, hasSaveError, saveAnswers } =
    useOnboardingSurvey();

  if (status === 'loading') {
    return <>{fallback}</>;
  }

  if (status === 'unanswered') {
    return (
      <OnboardingSurvey
        workspaceName={workspaceName}
        isSaving={isSaving}
        hasSaveError={hasSaveError}
        onSave={saveAnswers}
      />
    );
  }

  return <>{children}</>;
};

const GuidedOnboardingGate = ({
  children,
  fallback = null,
}: OnboardingSurveyGateProps) => {
  const { status, workspaceName, isSaving, hasSaveError, saveAnswers } =
    useOnboardingSurvey();
  const setGoal = useSetOnboardingGoal();
  const { enabled: projectsEnabled } = useFeatureFlag('projects', {
    enabled: false,
  });
  const { isWorkspaceAdmin, loading: isCheckingAdmin } = useIsWorkspaceAdmin();
  const companyStepEnabled = projectsEnabled && isWorkspaceAdmin;
  const [activeStep, setActiveStep] = useState<OnboardingStep | 'done' | null>(
    null,
  );
  const [furthestStepIndex, setFurthestStepIndex] = useState(0);

  if (
    activeStep === null &&
    (status === 'loading' || (status === 'unanswered' && isCheckingAdmin))
  ) {
    return <>{fallback}</>;
  }

  const enabledSteps: Record<OnboardingStep, boolean> = {
    about: true,
    company: companyStepEnabled,
  };
  const visibleSteps = ONBOARDING_STEPS.filter(step => enabledSteps[step.id]);
  const steps = visibleSteps.map(step => step.label);
  const firstStep = visibleSteps[0].id;
  const currentStep =
    activeStep === 'done'
      ? undefined
      : (activeStep ?? (status === 'unanswered' ? firstStep : undefined));

  if (!currentStep) {
    return <>{children}</>;
  }

  const stepIndex = visibleSteps.findIndex(step => step.id === currentStep);
  const progress = <OnboardingProgress steps={steps} currentStep={stepIndex} />;
  const mountedStepCount = Math.max(stepIndex, furthestStepIndex) + 1;

  const goToStep = (step: OnboardingStep | 'done'): void => {
    setActiveStep(step);
    const index = visibleSteps.findIndex(({ id }) => id === step);
    setFurthestStepIndex(previous => Math.max(previous, index));
  };

  const renderStep = (step: OnboardingStep): ReactNode => {
    if (step === 'company') {
      return (
        <Suspense fallback={fallback}>
          <ContextReview
            workspaceName={workspaceName}
            progress={progress}
            onBack={() => goToStep('about')}
            onContinue={() => goToStep('done')}
          />
        </Suspense>
      );
    }
    return (
      <AgentWelcome
        orgName={workspaceName}
        renderProgress={questionProgress => (
          <OnboardingProgress
            steps={steps}
            currentStep={stepIndex}
            currentStepProgress={questionProgress}
          />
        )}
        intro={
          isWorkspaceAdmin ? (
            <Suspense fallback={null}>
              <PlanIntro />
            </Suspense>
          ) : undefined
        }
        isSaving={isSaving}
        hasSaveError={hasSaveError}
        onContinue={async ({ goal, answers, startingPoint }) => {
          setGoal(goal, startingPoint);
          goToStep('about');
          if (await saveAnswers(answers)) {
            goToStep(companyStepEnabled ? 'company' : 'done');
          }
        }}
      />
    );
  };

  return (
    <>
      <VisuallyHidden aria-live="polite">
        {`Step ${stepIndex + 1} of ${steps.length}: ${steps[stepIndex]}`}
      </VisuallyHidden>
      {visibleSteps.slice(0, mountedStepCount).map(({ id }) => (
        <div key={id} hidden={id !== currentStep}>
          {renderStep(id)}
        </div>
      ))}
    </>
  );
};

const ChatsAwareOnboardingGate = (props: OnboardingSurveyGateProps) => (
  <FeatureFlagged name="chats">
    <FeatureFlagged.On>
      <GuidedOnboardingGate {...props} />
    </FeatureFlagged.On>
    <FeatureFlagged.Off>
      <SurveyGate {...props} />
    </FeatureFlagged.Off>
  </FeatureFlagged>
);

export const OnboardingSurveyGate = splitComponent<
  { enabled: boolean },
  OnboardingSurveyGateProps
>('guided-onboarding', { enabled: false }, ({ enabled }) =>
  enabled ? ChatsAwareOnboardingGate : SurveyGate,
);
